import React, { useCallback, useMemo } from 'react';
import { Menu, MenuItem, Paper, PaperProps } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import {
  TripLegType,
  TripLeg,
  Reservation,
} from '@/data/models/reservation.model';
import * as reservationSlice from '@/data/reservation.slice';
import * as tripLegSlice from '@/data/tripleg.slice';
import * as lookupSlice from '@/data/lookup.slice';
import TripLegDialog from '../reservations/TripLegDialog';
import { Passenger } from '@/data/models/passenger.model';
import { ServicePlan } from '@/data/models/service-plan.model';
import reservationUtils from '@/services/reservationUtils';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import CancelTripLegDialog from './CancelTripLegDialog';
import CancelTripLegUnavailableDialog from './CancelTripLegUnavailableDialog';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useIsSuperReaderUser } from '@/hooks/permissions/useIsSuperReaderUser';
import { usePortalName } from '@/hooks/usePortalName';
import ReservationAccordion from '@/components/home/passengers/ReservationAccordion';
import reservationService from '@/services/reservationService';

const PREFIX = 'ReservationList';

const classes = {
  container: `${PREFIX}-container`,
};

const StyledPaper = styled(Paper)(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'stretch',
    marginTop: theme.spacing(1),
    boxShadow: 'none',
  },
}));

interface ReservationTableProps extends PaperProps {
  passenger: Passenger | null;
  loadingPassenger: boolean;
  reservations: Reservation[];
  servicePlans: ServicePlan[];
}

export default function ReservationList({
  passenger,
  loadingPassenger,
  reservations,
  servicePlans,
  ...props
}: ReservationTableProps) {
  const t = useCommonTranslation();
  const dispatch = useAppDispatch();
  const isPortal = !!usePortalName();

  const isSuperReaderUser = useIsSuperReaderUser();

  const isSavingTrip = useAppSelector(reservationSlice.selectSavingTripLeg);
  const timezones = useAppSelector(lookupSlice.selectTimeZones);
  const [editOpen, setEditOpen] = React.useState(false);
  const [isAddingTripLeg, setIsAddingTripLeg] = React.useState(false);
  const [
    cancelTripLegUnavailableDialogOpen,
    setCancelTripLegUnavailableDialogOpen,
  ] = React.useState(false);
  const [tripLeg, setTripLeg] = React.useState<TripLeg>();
  const [tripType, setTripType] = React.useState<TripLegType>(
    TripLegType.MultiLeg,
  );
  const [editReservationReferenceID, setEditReservationReferenceID] =
    React.useState('');
  const [editTripLegSequence, setEditTripLegSequence] = React.useState(0);
  const [confirmOpen, setConfirmOpen] = React.useState(false);
  const [confirmTitle, setConfirmTitle] = React.useState('');
  const [confirmMessage, setConfirmMessage] = React.useState('');
  const [confirmReservation, setConfirmReservation] = React.useState(true);
  const [cancelPassengerUUID, setCancelPassengerUUID] = React.useState('');
  const [cancelReservationId, setCancelReservationId] = React.useState('');
  const [cancelTripId, setCancelTripId] = React.useState('');
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const stoMenuOpean = Boolean(anchorEl);
  const [cancelStandingOrderUUID, setCancelStandingOrderUUID] =
    React.useState<string>();
  const [
    cancelAllStandingOrderReservation,
    setCancelAllStandingOrderReservations,
  ] = React.useState(false);
  const syncedTripLegs = useAppSelector(tripLegSlice.selectTripLegDetails);
  const loadingSyncedTripLegs = useAppSelector(
    tripLegSlice.selectLoadingTripLegDetails,
  );

  const sortedReservations = useMemo(() => {
    const earliestTime = (res: Reservation) =>
      res.tripLegs?.reduce((earliest, leg) => {
        const ts = reservationUtils.getLegSortTime(leg);
        return earliest === 0 || (ts > 0 && ts < earliest) ? ts : earliest;
      }, 0) ?? 0;
    return [...reservations].sort((a, b) => earliestTime(a) - earliestTime(b));
  }, [reservations, timezones]);

  // Pending cancel state - handles both reservation and trip leg cancellation
  const [pendingCancel, setPendingCancel] = React.useState<{
    type: 'reservation' | 'tripLeg';
    tripLegReferenceId: string;
    reservationId: string;
  } | null>(null);

  // Effect to check editability after trip leg details are loaded
  React.useEffect(() => {
    if (pendingCancel && !loadingSyncedTripLegs) {
      // Cancel and Edit are using same logic
      const canEditTrip = reservationUtils.canPortalUserEditTripLeg(
        syncedTripLegs[pendingCancel.tripLegReferenceId],
        isPortal,
      );
      if (canEditTrip) {
        const currentPending = pendingCancel;
        setPendingCancel(null);

        // Common acrosss reservation and tripleg.
        setCancelPassengerUUID(passenger!.passengerUUID!);
        setCancelReservationId(currentPending.reservationId);

        if (currentPending.type === 'reservation') {
          setConfirmReservation(true);
          setConfirmTitle(t('passenger.cancelReservation'));
          setConfirmMessage(t('passenger.cancelReservationMessage'));
        } else {
          setCancelTripId(currentPending.tripLegReferenceId);
          setConfirmReservation(false);
          setConfirmTitle(t('passenger.cancelTripLeg'));
          setConfirmMessage(t('passenger.cancelTripLegMessage'));
        }
        setConfirmOpen(true);
      } else {
        // Show Cancel Trip Dialog
        setCancelTripLegUnavailableDialogOpen(true);
      }
    }
  }, [
    pendingCancel,
    loadingSyncedTripLegs,
    syncedTripLegs,
    isPortal,
    passenger,
  ]);

  const cancelReservation = useCallback(
    (id: string) => {
      const reservation = reservations.find(
        (x) => x.reservationReferenceID == id,
      );
      if (reservation && reservation.tripLegs.length > 0) {
        const firstLeg = reservation?.tripLegs.at(0);
        dispatch(
          tripLegSlice.getTripLegDetailsAsync(firstLeg?.tripLegReferenceID!),
        );
        setPendingCancel({
          type: 'reservation',
          tripLegReferenceId: firstLeg?.tripLegReferenceID!,
          reservationId: id,
        });
      }
    },
    [reservations, dispatch],
  );

  const cancelSTOReservation = (all: boolean) => {
    setCancelAllStandingOrderReservations(all);
    cancelReservation(cancelReservationId);
  };

  const onCancelReservation = useCallback(
    (evt: any, id: string, standingOrderUUID?: string) => {
      evt.stopPropagation();
      evt.preventDefault();
      if (standingOrderUUID) {
        setAnchorEl(evt.currentTarget);
        setCancelReservationId(id);
        setCancelStandingOrderUUID(standingOrderUUID);
      } else {
        cancelReservation(id);
      }
    },
    [cancelReservation],
  );

  const editTripLeg = useCallback(
    (leg: TripLeg) => {
      const isRoundTrip = !!leg.isRoundTrip;
      const pid = passenger!.passengerUUID!;
      const rid = leg.reservationReferenceID!;
      const lid = leg.tripLegReferenceID!;
      setEditReservationReferenceID(rid);
      setEditTripLegSequence(leg.tripLegSequence);
      reservationService.getTripLeg(pid, rid, lid).subscribe({
        next: (value) => {
          setTripLeg({
            ...value,
            reservation: {
              reservationReferenceID: leg?.reservation?.reservationReferenceID,
              standingOrderUUID: leg?.reservation?.standingOrderUUID,
            },
          });
          dispatch(reservationSlice.setCurrentTripLeg(value));
        },
      });
      setTripType(isRoundTrip ? TripLegType.RoundTrip : TripLegType.MultiLeg);
      setEditOpen(true);
    },
    [passenger, dispatch],
  );

  const cancelTripLeg = useCallback(
    (leg: TripLeg) => {
      dispatch(tripLegSlice.getTripLegDetailsAsync(leg.tripLegReferenceID!));
      setPendingCancel({
        type: 'tripLeg',
        tripLegReferenceId: leg.tripLegReferenceID!,
        reservationId: leg.reservationReferenceID!,
      });
    },
    [dispatch],
  );

  const onAddTripLeg = useCallback((evt: any, resid: string, seq: number) => {
    evt.stopPropagation();
    evt.preventDefault();
    setIsAddingTripLeg(true);
    setEditReservationReferenceID(resid);
    setEditTripLegSequence(seq);
    setTripType(TripLegType.MultiLeg);
    setTripLeg(undefined);
    setEditOpen(true);
  }, []);

  const handleEditDialogClose = () => {
    setIsAddingTripLeg(false);
    setEditOpen(false);
    setTripLeg(undefined);
    setEditReservationReferenceID('');
    setEditTripLegSequence(0);
  };

  const handleSTOCancelClose = () => {
    setAnchorEl(null);
  };

  const handleConfirmationClose = (data: any) => {
    const tripLegStatusReasonBCs: string[] = [];
    if (data) {
      Object.keys(data).forEach((key) => {
        if (data[key]) {
          tripLegStatusReasonBCs.push(data[key]);
        }
      });

      if (confirmReservation) {
        if (cancelAllStandingOrderReservation && cancelStandingOrderUUID) {
          dispatch(
            reservationSlice.cancelStandingOrderReservationAsync(
              cancelStandingOrderUUID,
              tripLegStatusReasonBCs,
            ),
          );
        } else {
          dispatch(
            reservationSlice.cancelReservationAsync(
              cancelPassengerUUID,
              cancelReservationId,
              tripLegStatusReasonBCs,
            ),
          );
        }
      } else {
        dispatch(
          reservationSlice.cancelTripLegAsync(
            cancelPassengerUUID,
            cancelReservationId,
            cancelTripId,
            tripLegStatusReasonBCs,
          ),
        );
      }
    }
    setCancelStandingOrderUUID(undefined);
    setCancelReservationId('');
    setConfirmOpen(false);
  };

  return (
    <StyledPaper className={classes.container} {...props}>
      {sortedReservations.map((res) => (
        <ReservationAccordion
          key={res.reservationReferenceID}
          reservation={res}
          passenger={passenger}
          isSuperReaderUser={isSuperReaderUser}
          loadingPassenger={loadingPassenger}
          isSavingTrip={isSavingTrip}
          timezones={timezones}
          onEditTripLeg={editTripLeg}
          onCancelTripLeg={cancelTripLeg}
          onAddTripLeg={onAddTripLeg}
          onCancelReservation={onCancelReservation}
        />
      ))}
      {passenger && (tripLeg || isAddingTripLeg) && (
        <TripLegDialog
          sequence={editTripLegSequence}
          reservationReferenceID={editReservationReferenceID}
          tripLeg={tripLeg}
          tripType={tripType}
          open={editOpen}
          passenger={passenger!}
          servicePlans={servicePlans}
          close={handleEditDialogClose}
        />
      )}
      <CancelTripLegDialog
        open={confirmOpen}
        close={handleConfirmationClose}
        dialogTitle={confirmTitle}
        dialogMessage={confirmMessage}
        submitDataFsElement={
          confirmReservation
            ? 'CancelReservationSubmitButton'
            : 'CancelTripSubmitButton'
        }
      />
      <CancelTripLegUnavailableDialog
        dialogOpen={cancelTripLegUnavailableDialogOpen || loadingSyncedTripLegs}
        loadingSyncedTripLeg={loadingSyncedTripLegs}
        showUnavailableMessage={cancelTripLegUnavailableDialogOpen}
        onClose={() => setCancelTripLegUnavailableDialogOpen(false)}
      />
      <Menu
        id="cancel-sto"
        anchorEl={anchorEl}
        open={stoMenuOpean}
        onClose={handleSTOCancelClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}>
        <MenuItem
          key={0}
          onClick={() => {
            handleSTOCancelClose();
            cancelSTOReservation(false);
          }}>
          {t('reservation.cancelSTOThis')}
        </MenuItem>
        <MenuItem
          key={1}
          onClick={() => {
            handleSTOCancelClose();
            cancelSTOReservation(true);
          }}>
          {t('reservation.cancelSTOAll')}
        </MenuItem>
      </Menu>
    </StyledPaper>
  );
}
